import React, { forwardRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, error, helperText, className = '', resize = 'vertical', ...props }, ref) => {
    const baseClasses = [
      'block w-full rounded-md border-0 py-1.5 px-3',
      'text-gray-900 dark:text-white',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'bg-white dark:bg-gray-800',
      'shadow-sm ring-1 ring-inset',
      'focus:ring-2 focus:ring-inset focus:outline-none',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'sm:text-sm sm:leading-6',
    ];

    const resizeClasses = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const stateClasses = error
      ? [
          'ring-red-300 dark:ring-red-600',
          'focus:ring-red-600 dark:focus:ring-red-500',
          'text-red-900 dark:text-red-100',
          'placeholder:text-red-300 dark:placeholder:text-red-400',
        ]
      : ['ring-gray-300 dark:ring-gray-600', 'focus:ring-primary-600 dark:focus:ring-primary-500'];

    const textareaClasses = [
      ...baseClasses,
      ...stateClasses,
      resizeClasses[resize],
      className,
    ].join(' ');

    return (
      <div>
        {label && (
          <label className='block text-sm font-medium leading-6 text-gray-900 dark:text-white mb-2'>
            {label}
          </label>
        )}
        <div className='relative'>
          <textarea
            ref={ref}
            className={textareaClasses}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? `${props.id}-error` : helperText ? `${props.id}-description` : undefined
            }
            {...props}
          />
          {error && (
            <div className='pointer-events-none absolute inset-y-0 right-0 flex items-start pr-3 pt-2'>
              <ExclamationCircleIcon className='h-5 w-5 text-red-500' aria-hidden='true' />
            </div>
          )}
        </div>
        {error && (
          <p className='mt-2 text-sm text-red-600 dark:text-red-400' id={`${props.id}-error`}>
            {error}
          </p>
        )}
        {!error && helperText && (
          <p
            className='mt-2 text-sm text-gray-500 dark:text-gray-400'
            id={`${props.id}-description`}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
